import React, { useState, useEffect } from 'react';
import './LeaveHistoryReport.css';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { selectStyles } from '../../../assets/Styles/selectStyle';
import { capitalizeFirstLetter } from '../../../utils/validations';
import { getStates } from '../../../services/monthlyAttendanceService';
import { getDistrict } from '../../../services/monthlyAttendanceService';
import { getEmployyesforTransfor } from '../../../services/transport';
import { getDesignationsForEmployees } from '../../../services/shiftsService';
import { leaveHistroyReport } from '../../../services/leaveService';

const LeaveHistoryReport = () => {
    const [isFocused, setIsFocused] = useState({
        state: false,
        district: false,
        tillDate: false,
        employeeName: false,
        designationForEmp: false
    });

    const date = new Date();
    const [state, setState] = useState(3);
    const [stateOptions, setStateOptions] = useState([]);
    const [district, setDistrict] = useState(null);
    const [districtOptions, setDistrictOptions] = useState([]);
    const [errors, setErrors] = useState({});
    const [tillDate, setTillDate] = useState(date);
    const [reportData, setReportData] = useState(null); // Changed to store object, not array
    const [loading, setLoading] = useState(false);
    const [desginationsForEmployees, setDesignationForEmployees] = useState([]);
    const [seletedDesignation, setSelectedDesignation] = useState('');
    const [employees, setEmployees] = useState([]);
    const [employeeName, setEmployeeName] = useState(null);
    const [draftId, setDraftId] = useState("");
    const [designation, setDesignation] = useState("");

    useEffect(() => {
        const fetchStates = async () => {
            try {
                const res = await getStates();
                setStateOptions(res.map(item => ({
                    value: item.id,
                    label: capitalizeFirstLetter(item.stateName)
                })));
            } catch (err) {
                setErrors(prev => ({ ...prev, general: "Failed to load states" }));
            }
        };
        fetchStates();
    }, []);

    useEffect(() => {
        if (state) {
            const fetchDistricts = async () => {
                try {
                    const data = await getDistrict(state);
                    setDistrictOptions(
                        data.map(item => ({
                            value: item.districtId,
                            label: item.districtName,
                        }))
                    );
                } catch (error) {
                    console.error('Error fetching districts:', error);
                    setErrors(prev => ({ ...prev, general: 'Failed to load districts' }));
                }
            };
            fetchDistricts();
        } else {
            setDistrictOptions([]);
            setDistrict(null);
        }
    }, [state]);

    useEffect(() => {
        const fetchDesignationForEmployyes = async () => {
            try {
                const response = await getDesignationsForEmployees();
                const options = response.map(item => ({
                    value: item.id,
                    label: item.designationName,
                    shortName: item.designationShortName,
                }));
                setDesignationForEmployees(options);
            } catch (error) {
                console.log("Get all Designations before fetching ");
            }
        };
        fetchDesignationForEmployyes();
    }, []);

    useEffect(() => {
        const fetchEmployees = async () => {
            try {
                const response = await getEmployyesforTransfor(state, district, seletedDesignation);
                const data = response.data;

                if (Array.isArray(data)) {
                    setEmployees(
                        data.map((item) => ({
                            value: item.employeeUniqueId,
                            label: `${item.fullName} - ${item.contactNumber}`,
                            designation: item.designation || "",
                            presentState: item.stateName || "",
                            presentDistrict: item.districtName || "",
                            workLocations: item.workLocations || [],
                            godowns: item.godowns || [],
                            draftId: item.employeeUniqueId,
                            raw: item
                        }))
                    );
                }
            } catch (error) {
                console.error("Error fetching employees:", error);
                setErrors(prev => ({ ...prev, general: "Error fetching employees" }));
            }
        };

        if (district) {
            fetchEmployees();
        } else {
            setEmployees([]);
        }
    }, [district, state, seletedDesignation]);

    const handleInputChange = (selectedOption, field) => {
        const value = selectedOption ? selectedOption.value : null;

        switch (field) {
            case 'state':
                setState(value);
                setDistrict(null);
                setEmployeeName(null);
                setReportData(null);
                break;
            case 'district':
                setDistrict(value);
                setEmployeeName(null);
                setReportData(null);
                break;
            case "employeeName":
                setEmployeeName(selectedOption ? selectedOption.value : null);
                if (selectedOption) {
                    setDesignation(selectedOption.designation || "");
                    setDraftId(selectedOption.draftId || "");
                } else {
                    setDesignation("");
                    setDraftId("");
                    setReportData(null);
                }
                break;
            case 'designationForEmp':
                setSelectedDesignation(value);
                setDraftId("");
                setEmployeeName(null);
                setReportData(null);
                break;
            default:
                break;
        }
        setErrors(prev => ({ ...prev, [field]: null }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!state) newErrors.state = "State is required";
        if (!district) newErrors.district = "District is required";
        if (!employeeName) newErrors.employeeName = "Employee Name is required";
        if (!tillDate) newErrors.tillDate = "Till Date is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleClear = () => {
        setState(null);
        setDistrict(null);
        setSelectedDesignation('');
        setEmployeeName(null);
        setDraftId("");
        setTillDate(date);
        setReportData(null);
        setErrors({});
    };

    const handleGetReport = async () => {
        if (!validateForm()) return;

        setLoading(true);
        try {
            const response = await leaveHistroyReport(state, district, draftId, tillDate);
            setReportData(response);
            
            if (!response || !response.history || response.history.length === 0) {
                setErrors(prev => ({ ...prev, general: "No leave history found for selected employee" }));
            } else {
                setErrors(prev => ({ ...prev, general: null }));
            }
        } catch (err) {
            console.error("Error fetching report:", err);
            setErrors(prev => ({
                ...prev,
                general: err.response?.data?.message || err.message || "Failed to fetch leave history"
            }));
            setReportData(null);
        } finally {
            setLoading(false);
        }
    };

    const exportToExcel = () => {
        if (!reportData || !reportData.history || reportData.history.length === 0) return;

        const exportData = reportData.history.map((item, index) => ({
            'S.No': index + 1,
            'Employee Name': reportData.employeeName,
            'Employee ID': reportData.employeeId,
            'Contact Number': item.contactNumber,
            'District': item.districtName,
            'From Date': format(new Date(item.fromDate), 'dd-MM-yyyy'),
            'To Date': format(new Date(item.toDate), 'dd-MM-yyyy'),
            'Days Applied': item.daysApplied,
            'Leave Type': item.type,
            'Reason': item.reason || '-',
            'Status': item.status,
            'Remarks': item.remarks || '-'
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Leave History');
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(data, `Leave_History_${reportData.employeeName}_${format(new Date(), 'dd-MM-yyyy')}.xlsx`);
    };

    return (
        <div className="employee-leave-report-maincontainer">
            <div className="employee-leave-report-box">
                <h2 className="employee-leave-report-heading">Leave History</h2>
                {errors.general && <div className="error-message">{errors.general}</div>}

                <div className="employee-leave-report-structure-heading">
                    <div className="employee-leave-report-maincontainer-1">
                        <div className="employee-leave-report-container-part">
                            <div className={`employee-leave-report-floating-label-container-2 ${isFocused.state || state ? 'float-label' : ''}`}>
                                <Select
                                    name="state"
                                    options={stateOptions}
                                    value={stateOptions.find(opt => opt.value === state) || null}
                                    onChange={option => handleInputChange(option, 'state')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, state: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, state: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                />
                                <label><span className="required">*</span>State</label>
                                {errors.state && <div className="error-message">{errors.state}</div>}
                            </div>
                        </div>
                        <div className="employee-leave-report-container-part">
                            <div className={`employee-leave-report-floating-label-container-2 ${isFocused.district || district ? 'float-label' : ''}`}>
                                <Select
                                    name="district"
                                    options={districtOptions}
                                    value={districtOptions.find(opt => opt.value === district) || null}
                                    onChange={option => handleInputChange(option, 'district')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, district: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, district: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                />
                                <label><span className="required">*</span>District</label>
                                {errors.district && <div className="error-message">{errors.district}</div>}
                            </div>
                        </div>
                    </div>

                    <div className="employee-termination-maincontainer-1">
                        <div className="employee-termination-container-part">
                            <div className={`employee-termination-floating-label-container-2 ${isFocused.seletedDesignation || seletedDesignation ? "float-label" : ""}`}>
                                <Select
                                    name='designation'
                                    options={desginationsForEmployees}
                                    value={desginationsForEmployees.find(opt => opt.value === seletedDesignation) || null}
                                    onChange={(option) => handleInputChange(option, "designationForEmp")}
                                    isClearable
                                    onFocus={() => setIsFocused(prev => ({ ...prev, designationForEmp: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, designationForEmp: false }))}
                                    placeholder=" "
                                    styles={selectStyles}
                                />
                                <label>Designation</label>
                            </div>
                        </div>

                        <div className="employee-termination-container-part">
                            <div className={`employee-termination-floating-label-container-2 ${isFocused.employeeName || employeeName ? "float-label" : ""}`}>
                                <Select
                                    name="employeeName"
                                    options={employees}
                                    value={employees.find(opt => opt.value === employeeName) || null}
                                    onChange={(option) => handleInputChange(option, "employeeName")}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, employeeName: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, employeeName: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                />
                                <label><span className="required">*</span>Employee Name</label>
                                {errors.employeeName && <div className="error-message">{errors.employeeName}</div>}
                            </div>
                        </div>
                    </div>

                    <div className="employee-leave-report-maincontainer-1">
                        <div className="employee-leave-report-container-part">
                            <div className={`employee-leave-report-floating-label-container-2 ${isFocused.tillDate || tillDate ? "float-label" : ""}`}>
                                <DatePicker
                                    selected={tillDate}
                                    onChange={(date) => setTillDate(date)}
                                    dateFormat="dd-MM-yyyy"
                                    placeholderText=""
                                    className="did-floating-input"
                                    maxDate={new Date()}
                                />
                                <label><span className="required">*</span>Till Date</label>
                                {errors.tillDate && <div className="error-message">{errors.tillDate}</div>}
                            </div>
                        </div>
                        <div className="employee-leave-report-container-part">
                            <div className="employee-leave-report-btn-div">
                                <button type="button" className="employee-leave-report-cancel" onClick={handleClear}>
                                    Clear
                                </button>
                                <button type="button" className="employee-leave-report-btn" onClick={handleGetReport} disabled={loading}>
                                    {loading ? 'Loading...' : 'Get Report'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Leave Summary Cards - Display at the top */}
            {reportData && reportData.history && reportData.history.length > 0 && (
                <>
                    <div className="leave-summary-cards">
                        <div className="summary-card">
                            <h4>Employee Name</h4>
                            <p>{reportData.employeeName || '-'}</p>
                        </div>
                        <div className="summary-card">
                            <h4>Employee ID</h4>
                            <p>{reportData.employeeId || '-'}</p>
                        </div>
                        <div className="summary-card">
                            <h4>Total Leaves</h4>
                            <p>{reportData.totalLeaves || 0}</p>
                        </div>
                        <div className="summary-card">
                            <h4>Used Leaves</h4>
                            <p>{reportData.usedLeaves || 0}</p>
                        </div>
                        <div className="summary-card">
                            <h4>Remaining Leaves</h4>
                            <p>{reportData.remainingLeaves || 0}</p>
                        </div>
                        {/* <div className="summary-card">
                            <h4>Eligible Leaves</h4>
                            <p>{reportData.eligibleLeaves || 0}</p>
                        </div> */}
                        <div className="summary-card">
                            <h4>Available Leaves</h4>
                            <p>{reportData.availableLeaves || 0}</p>
                        </div>
                    </div>

                    {/* Export Button */}
                    {/* <div className="export-button-container">
                        <button type="button" className="export-excel-btn" onClick={exportToExcel}>
                            📊 Export to Excel
                        </button>
                    </div> */}

                    {/* Leave History Table */}
                    <div className="employee-leave-report-table-container">
                        <div className="employee-leave-report-table-wrapper">
                            <table className="employee-leave-report-table">
                                <thead>
                                    <tr>
                                        <th>S.No</th>
                                        <th>From Date</th>
                                        <th>To Date</th>
                                        <th>Days Applied</th>
                                        <th>Leave Type</th>
                                        <th>Reason</th>
                                        <th>Status</th>
                                        <th>Remarks</th>
                                        <th>Backup Person</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reportData.history.map((item, index) => (
                                        <tr key={item.id}>
                                            <td>{index + 1}</td>
                                            <td>{format(new Date(item.fromDate), 'dd-MM-yyyy')}</td>
                                            <td>{format(new Date(item.toDate), 'dd-MM-yyyy')}</td>
                                            <td>{item.daysApplied}</td>
                                            <td>{item.type}</td>
                                            <td>{item.reason || '-'}</td>
                                            <td>
                                                <span className={`status-badge status-${item.status?.toLowerCase()}`}>
                                                    {item.status}
                                                </span>
                                            </td>
                                            <td>{item.remarks || '-'}</td>
                                            <td>
                                                {item.backupPersonName ? (
                                                    <div>
                                                        <div>{item.backupPersonName}</div>
                                                        <small>{item.backupPersonContactNumber}</small>
                                                    </div>
                                                ) : '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            {/* No Data Message */}
            {reportData && (!reportData.history || reportData.history.length === 0) && (
                <div className="no-data-message">
                    No leave history found for this employee
                </div>
            )}
        </div>
    );
};

export default LeaveHistoryReport;